import { GetJobStatusResponse } from '@upright/common/internal/jobs/api-types';
import { JobStatus } from '@upright/common/internal/jobs/types';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { fetchJobStatus } from '.';

type PollingBehavior = 'always' | 'whenNotComplete' | 'never';
type QueryOptions = Omit<UseQueryOptions<GetJobStatusResponse, Error>, 'queryKey' | 'queryFn'>;

export function useFetchJobStatus(
  jobId: number | undefined,
  pollingBehavior: PollingBehavior = 'whenNotComplete',
  options?: QueryOptions & {
    onSuccess?: (response: GetJobStatusResponse) => void;
  },
): UseQueryResult<GetJobStatusResponse, Error> & {
  resetPolling: () => void;
} {
  const [isPolling, setIsPolling] = useState(pollingBehavior === 'always');

  const query = useQuery<GetJobStatusResponse, Error>({
    ...(options || {}),
    queryKey: ['fetch-job-status', jobId],
    queryFn: async () => {
      if (!jobId) throw Error(`JobId required`);
      const response = await fetchJobStatus({ jobId });
      return response;
    },
    enabled: !isNil(jobId) && (!isNil(options?.enabled) ? options.enabled : true),
    refetchInterval: isPolling ? options?.refetchInterval || 3000 : false,
  });

  useEffect(() => {
    if (query.isSuccess) {
      const isComplete = !!query.data.status && getIsJobComplete(query.data.status);
      if (pollingBehavior === 'whenNotComplete') setIsPolling(!isComplete);

      if (options?.onSuccess) {
        options.onSuccess(query.data);
      }
    }
    // OH: Stop polling if the endpoint fails to return the status
    if (query.isError) {
      setIsPolling(false);
    }
  }, [options, pollingBehavior, query.data, query.isSuccess, query.isError]);

  const resetPolling = () => {
    setIsPolling(true);
    void query.refetch();
  };

  return { ...query, resetPolling };
}

const completedJobStatuses = [JobStatus.SUCCESS, JobStatus.ERROR];

export function getIsJobComplete(jobStatus: JobStatus | undefined) {
  if (isNil(jobStatus)) return false;
  return completedJobStatuses.includes(jobStatus);
}
